import React, { createContext, useContext, useEffect, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";

// App Registration (Entra ID) do DIG61.
const CLIENT_ID = "18ab116a-e4b4-46ea-b2e8-45f1688133ff";
const TENANT_ID = "963ca415-7dad-4569-b6df-554fbb071304";

// Autenticação via MSAL (Authorization Code + PKCE tratado pela lib). O
// ID token do Entra ID é exposto via getToken() (contexto useAuth) e enviado
// pelo frontend como Authorization: Bearer no upload; o backend valida a
// assinatura (JWKS do tenant), issuer e audience antes de aceitar o arquivo.
// A sessão do app não tem prazo próprio: ela acompanha a sessão real do
// Entra ID. A cada carregamento -- e periodicamente enquanto a aba fica
// aberta -- validamos a conta salva no localStorage com acquireTokenSilent:
// se a sessão da Microsoft ainda estiver ativa, renova sozinha e mantém
// logado; se tiver expirado ou o acesso tiver sido revogado, a tela volta a
// pedir login sem exigir nenhuma ação da pessoa.
const SILENT_SCOPES = ["openid", "profile", "email"];
const REVALIDATE_INTERVAL_MS = 15 * 60 * 1000;

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "localStorage",
  },
});

// Confirma junto ao Entra ID que a conta salva ainda tem sessão válida.
// Retorna a conta se sim, ou null se precisar de login novo.
async function validateSession() {
  const contas = msalInstance.getAllAccounts();
  if (contas.length === 0) return null;
  const account = contas[0];
  try {
    await msalInstance.acquireTokenSilent({ scopes: SILENT_SCOPES, account });
    return account;
  } catch {
    return null;
  }
}

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

let msalReadyPromise = null;
function ensureMsalReady() {
  if (!msalReadyPromise) {
    msalReadyPromise = msalInstance.initialize().then(() => msalInstance.handleRedirectPromise());
  }
  return msalReadyPromise;
}

function LoginGate({ children }) {
  const [status, setStatus] = useState("checking");
  const [account, setAccount] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Encerra a sessão do app localmente (sem chamar logout no Entra ID).
  // Usado quando a revalidação silenciosa falha -- ex: sessão da Microsoft
  // expirou ou o acesso foi revogado no AD -- ou quando o backend rejeita o
  // token (getToken()/upload). "motivo", quando informado, aparece como erro
  // na tela de login que a pessoa vê em seguida.
  const encerrarSessaoLocal = async (motivo) => {
    try {
      await msalInstance.clearCache();
    } catch (err) {
      console.error("Falha ao limpar o cache do MSAL:", err);
    }
    setAccount(null);
    setStatus("anonymous");
    if (motivo) setErrorMsg(motivo);
  };

  useEffect(() => {
    ensureMsalReady()
      .then(async () => {
        const contaValida = await validateSession();
        if (contaValida) {
          msalInstance.setActiveAccount(contaValida);
          setAccount(contaValida);
          setStatus("authenticated");
        } else {
          await encerrarSessaoLocal();
        }
      })
      .catch((err) => {
        setErrorMsg(err.message);
        setStatus("anonymous");
      });
  }, []);

  // Enquanto a aba fica aberta, revalida periodicamente contra o Entra ID --
  // se a sessão da Microsoft cair (logout em outro lugar, acesso revogado no
  // AD, etc.), a tela volta a pedir login sozinha avisando o motivo.
  useEffect(() => {
    if (status !== "authenticated") return;
    const timer = setInterval(async () => {
      const contaValida = await validateSession();
      if (!contaValida) {
        encerrarSessaoLocal("Sua sessão expirou. Faça login novamente.");
      }
    }, REVALIDATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [status]);

  const handleLogin = async () => {
    try {
      setErrorMsg("");
      await ensureMsalReady();
      // prompt: "select_account" mostra o seletor de contas. Com a sessão da
      // Microsoft ainda ativa, entrar vira um clique -- sem digitar senha.
      await msalInstance.loginRedirect({
        scopes: SILENT_SCOPES,
        prompt: "select_account",
      });
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  const handleLogout = () => {
    msalInstance.logoutRedirect();
  };

  // ID token do Entra ID para a conta ativa, renovado silenciosamente pelo
  // MSAL quando necessário. Usado como Authorization: Bearer no backend, que
  // valida a assinatura (JWKS do tenant) antes de aceitar qualquer upload.
  const getToken = async () => {
    const contaAtiva = msalInstance.getActiveAccount() || account;
    if (!contaAtiva) {
      throw new Error("Sessão expirada. Faça login novamente.");
    }
    const resultado = await msalInstance.acquireTokenSilent({
      scopes: SILENT_SCOPES,
      account: contaAtiva,
    });
    return resultado.idToken;
  };

  if (status === "checking") {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <p className="text-slate-600 font-medium">Verificando sessão...</p>
      </div>
    );
  }

  if (status === "authenticated") {
    return (
      <AuthContext.Provider
        value={{ account, logout: handleLogout, getToken, expirarSessao: encerrarSessaoLocal }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <div className="flex flex-col h-screen items-center justify-start bg-slate-50 pt-20">
      <img src="logo-tigre.png" alt="logo-tigre" className="w-48" />
      <div className="rounded-xl bg-white p-10 shadow-md text-center max-w-md">
        <h1 className="text-xl font-semibold text-slate-800 mb-2">DIG61 - Envio de Notas GISS</h1>
        <p className="text-slate-500 mb-6">Faça login com sua conta corporativa.</p>
        {errorMsg && <p className="text-sm text-red-600 mb-4">{errorMsg}</p>}
        <button
          onClick={handleLogin}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2 rounded-lg transition"
        >
          Entrar com Microsoft
        </button>
      </div>
      <img
        src="logo-biti9.png"
        alt="logo-biti9"
        className="fixed bottom-4 right-4 w-24"
      />
    </div>
  );
}

export default function AuthProvider({ children }) {
  return <LoginGate>{children}</LoginGate>;
}
